import { DeleteOutline } from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { get } from 'lodash';
import React from 'react';
import {
  addWalletAddress,
  deleteWalletAddress,
} from '../../../http/WalletsApi';
import {
  CHAIN_LABELS,
  CHAINS,
  validateAddressValue,
  VALUE_PLACEHOLDERS,
} from '../addressValidation';

const initialForm = Object.freeze({ chain: CHAINS.ETHEREUM, value: '' });

export default function ManageAddressesDialog({
  open,
  onClose,
  wallet,
  onAddressesChange,
  setErrMessage,
  setSnackOpen,
}) {
  const [addresses, setAddresses] = React.useState([]);
  const [form, setForm] = React.useState(initialForm);
  const [fieldError, setFieldError] = React.useState(null);
  const [submitting, setSubmitting] = React.useState(false);

  const walletId = wallet?.id;

  const showError = React.useCallback(
    (err, fallback) => {
      setErrMessage(get(err, 'response.data.message', fallback));
      setSnackOpen(true);
    },
    [setErrMessage, setSnackOpen],
  );

  React.useEffect(() => {
    if (!open) return;
    // The wallet list endpoint already embeds addresses, so seed from the wallet
    // we were given instead of refetching them here.
    setAddresses(wallet?.addresses || []);
    setForm(initialForm);
    setFieldError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, walletId]);

  const handleChainChange = (e) => {
    setForm((prev) => ({ ...prev, chain: e.target.value }));
    setFieldError(null);
  };

  const handleValueChange = (e) => {
    setForm((prev) => ({ ...prev, value: e.target.value }));
    if (fieldError) setFieldError(null);
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    const value = form.value.trim();
    const validationError = validateAddressValue(form.chain, value);
    if (validationError) {
      setFieldError(validationError);
      return;
    }

    setSubmitting(true);
    try {
      const { data } = await addWalletAddress(walletId, {
        chain: form.chain,
        value,
      });
      const next = [...addresses, data];
      setAddresses(next);
      onAddressesChange(walletId, next);
      setForm(initialForm);
    } catch (err) {
      const status = get(err, 'response.status');
      const fallback =
        status === 409
          ? 'That address is already registered'
          : 'Could not add address';
      showError(err, fallback);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (addressId) => {
    try {
      await deleteWalletAddress(walletId, addressId);
      const next = addresses.filter((a) => a.id !== addressId);
      setAddresses(next);
      onAddressesChange(walletId, next);
    } catch (err) {
      showError(err, 'Could not delete address');
    }
  };

  return (
    <Dialog fullWidth open={open} onClose={onClose}>
      <DialogTitle>Manage addresses{wallet?.name ? ` · ${wallet.name}` : ''}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {addresses.length > 0 ? (
            <List dense disablePadding>
              {addresses.map((address) => (
                <ListItem
                  key={address.id}
                  divider
                  secondaryAction={
                    <IconButton
                      edge="end"
                      size="small"
                      title="Delete address"
                      onClick={() => handleDelete(address.id)}
                    >
                      <DeleteOutline />
                    </IconButton>
                  }
                >
                  <Chip
                    size="small"
                    label={CHAIN_LABELS[address.chain] || address.chain}
                    sx={{ mr: 1 }}
                  />
                  <ListItemText
                    primaryTypographyProps={{
                      variant: 'body2',
                      sx: { wordBreak: 'break-all' },
                    }}
                    primary={address.value}
                    secondary={
                      address.chain === CHAINS.BITCOIN
                        ? 'Balance not computed yet'
                        : undefined
                    }
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No addresses yet.
            </Typography>
          )}

          <Box component="form" onSubmit={handleAdd}>
            <Stack spacing={2}>
              <TextField
                select
                label="Chain"
                value={form.chain}
                onChange={handleChainChange}
                fullWidth
              >
                {Object.values(CHAINS).map((chain) => (
                  <MenuItem key={chain} value={chain}>
                    {CHAIN_LABELS[chain]}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Address value"
                value={form.value}
                onChange={handleValueChange}
                placeholder={VALUE_PLACEHOLDERS[form.chain]}
                error={Boolean(fieldError)}
                helperText={fieldError || ' '}
                fullWidth
                required
              />
              <Button type="submit" variant="contained" disabled={submitting}>
                Add address
              </Button>
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
